import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { trackPromoReveal, trackPromoCopy } from '../../utils/promoStats';
import { openPartnerLink } from '../../utils/openPartnerLink';
import { getBestDiscountLabel, getPromoActionLabel, getPromoBadgeLabel, getPromoBadgeTextClass } from '../../utils/promoUi';
import { useVipBadges } from '../../hooks/useVipBadges';
import VipBadge from '../../components/base/VipBadge';
import { readPersistedStores } from '../../utils/storeSeed';
import { buildStoreSeo, usePageSeo } from '../../utils/seo';

interface StorePromo {
  id: string;
  title: string;
  code: string;
  discount: string;
  description: string;
  expiryDate: string;
  isExclusive: boolean;
  partnerUrl: string;
  isActive: boolean;
}

interface Store {
  id: number;
  name: string;
  logo: string;
  description: string;
  category: string;
  subcategory?: string;
  rating: number;
  isActive: boolean;
  createdAt: string;
  promos: StorePromo[];
}

export default function StoreDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [revealedCodes, setRevealedCodes] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [store, setStore] = useState<Store | null>(null);
  const { getVipBadge } = useVipBadges();

  const storeSeo = store
    ? buildStoreSeo(
        store.name,
        store.category,
        store.promos.filter((promo) => promo.isActive).length,
        getBestDiscountLabel(store.promos.filter((promo) => promo.isActive).map((promo) => promo.discount)),
      )
    : {
        title: 'Магазин не найден | Promokod.online',
        description: 'Запрошенный магазин не найден или недоступен в публичной версии Promokod.online.',
      };

  usePageSeo({
    title: storeSeo.title,
    description: storeSeo.description,
    canonicalPath: slug ? `/store/${slug}` : '/stores',
    noIndex: !store,
  });

  useEffect(() => {
    if (!slug) return;

    // Загружаем магазины из localStorage (приоритет admin_stores_v2)
    const allStores = readPersistedStores(localStorage) as Store[];
    
    // Ищем магазин по slug
    const foundStore = allStores.find((s: Store) => {
      const storeSlug = s.name.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9а-яё\-]/gi, '')
        .replace(/\.ru$/, '')
        .replace(/\.com$/, '');
      return storeSlug === slug && s.isActive;
    });

    if (foundStore) {
      setStore(foundStore);
    }
  }, [slug]);

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-24 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-store-line text-3xl text-gray-400"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Магазин не найден</h2>
          <p className="text-gray-600 mb-8">К сожалению, такой магазин не найден в нашей базе</p>
          <button
            onClick={() => navigate('/stores')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Все магазины
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const activePromos = store.promos.filter(promo => promo.isActive);

  const handleRevealCode = (promoId: string, code: string, partnerUrl: string) => {
    setRevealedCodes(prev => new Set([...prev, promoId]));
    if (code) {
      navigator.clipboard.writeText(code);
    }
    setCopiedId(promoId);
    setTimeout(() => setCopiedId(null), 2000);

    // Track reveal + copy
    const promo = activePromos.find(p => p.id === promoId);
    if (promo && store) {
      const trackData = {
        promoId,
        promoCode: code,
        promoTitle: promo.title,
        storeName: store.name,
        storeId: String(store.id),
        discount: promo.discount,
      };
      trackPromoReveal(trackData);
      trackPromoCopy(trackData);
    }

    openPartnerLink(partnerUrl);
  };

  const handleCopyCode = (promoId: string, code: string) => {
    if (!code) {
      return;
    }

    navigator.clipboard.writeText(code);
    setCopiedId(promoId);
    setTimeout(() => setCopiedId(null), 2000);

    // Track copy
    const promo = activePromos.find(p => p.id === promoId);
    if (promo && store) {
      trackPromoCopy({
        promoId,
        promoCode: code,
        promoTitle: promo.title,
        storeName: store.name,
        storeId: String(store.id),
        discount: promo.discount,
      });
    }
  };

  const getMaxDiscount = (): string => {
    if (!activePromos || activePromos.length === 0) return '0%';

    return getBestDiscountLabel(activePromos.map((promo) => promo.discount));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-14">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-blue-200 mb-6">
            <a href="/stores" onClick={(e) => { e.preventDefault(); navigate('/stores'); }} className="hover:text-white transition-colors cursor-pointer">Магазины</a>
            <i className="ri-arrow-right-s-line"></i>
            <a href={`/stores?category=${encodeURIComponent(store.category)}`} onClick={(e) => { e.preventDefault(); navigate(`/stores?category=${encodeURIComponent(store.category)}`); }} className="hover:text-white transition-colors cursor-pointer">{store.category}</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">{store.name}</span>
          </div>
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 overflow-hidden">
              <img src={store.logo} alt={store.name} className="w-full h-full object-contain rounded-xl p-2" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="text-3xl md:text-4xl font-bold">{store.name}</h1>
                {(() => {
                  const vip = getVipBadge(String(store.id));
                  return vip ? <VipBadge badge={vip.badge} badgeColor={vip.badgeColor} size="md" /> : null;
                })()}
              </div>
              <p className="text-blue-100 text-lg mb-3">{store.description}</p>
              <div className="flex items-center gap-4 text-sm">
                {store.rating > 0 && (
                  <>
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-400"></i>
                      <span className="font-medium">{store.rating}</span>
                    </div>
                    <span className="text-blue-200">•</span>
                  </>
                )}
                <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-medium">{store.category}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Промокоды */}
            <div className="lg:col-span-2 space-y-5">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Все промокоды {store.name}
              </h2>

              {activePromos.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
                  <i className="ri-coupon-line text-6xl text-gray-300 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Промокоды скоро появятся</h3>
                  <p className="text-gray-600">В данный момент для этого магазина нет активных промокодов</p>
                </div>
              ) : (
                activePromos.map((promo) => (
                  <div
                    key={promo.id}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                  >
                    <div className="flex">
                      {/* Левая полоса со скидкой */}
                      <div className="w-28 flex-shrink-0 bg-gradient-to-b from-blue-600 to-blue-700 flex flex-col items-center justify-center text-white p-4">
                        <span className={`${getPromoBadgeTextClass(promo.discount)} font-bold text-center break-words whitespace-normal`}>
                          {getPromoBadgeLabel(promo.discount)}
                        </span>
                      </div>

                      {/* Контент */}
                      <div className="flex-1 p-5">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{promo.title}</h3>
                              {promo.isExclusive && (
                                <span className="bg-orange-100 text-orange-600 text-xs px-2 py-0.5 rounded-full font-medium">
                                  Эксклюзив
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed">{promo.description}</p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-4">
                          <span className="text-xs text-gray-400">
                            <i className="ri-time-line mr-1"></i>
                            Действует до {promo.expiryDate}
                          </span>

                          <div className="flex items-center gap-3">
                            {revealedCodes.has(promo.id) ? (
                              <div className="flex items-center gap-2">
                                <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg px-4 py-2">
                                  <span className="font-mono font-bold text-gray-900 tracking-wider">{promo.code}</span>
                                </div>
                                <button
                                  onClick={() => handleCopyCode(promo.id, promo.code)}
                                  className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
                                >
                                  <i className={copiedId === promo.id ? 'ri-check-line text-green-600' : 'ri-file-copy-line'}></i>
                                  {copiedId === promo.id ? 'Скопировано' : 'Копировать'}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleRevealCode(promo.id, promo.code, promo.partnerUrl)}
                                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap font-medium text-sm"
                              >
                                <i className="ri-coupon-line"></i>
                                {getPromoActionLabel(promo.code)}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Сайдбар */}
            <div className="space-y-6">
              {/* О магазине */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                <h3 className="font-semibold text-gray-900 mb-3">О магазине</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{store.description}</p>
              </div>

              {/* Статистика */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Статистика</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Активных промокодов</span>
                    <span className="font-semibold text-gray-900">{activePromos.length}</span>
                  </div>
                  {store.rating > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Рейтинг</span>
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-yellow-400 text-xs"></i>
                        <span className="font-semibold text-gray-900">{store.rating}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Категория</span>
                    <span className="font-semibold text-gray-900">{store.category}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Макс. скидка</span>
                    <span className="font-semibold text-green-600">{getMaxDiscount()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
